import { useEffect, useMemo, useState } from "react";
import DatePicker from "react-multi-date-picker";
import "react-multi-date-picker/styles/colors/teal.css";
import { useTranslation } from "react-i18next";
import "./MyProperties.css";

/* ✅ keep one API base */
const API_URL =
  process.env.NODE_ENV === "production"
    ? process.env.REACT_APP_API_URL || "https://api.hellastay.com"
    : "http://localhost:8000";

/* ===================== ✅ AUTH (cookie + Bearer fallback) ===================== */
const getToken = () => {
  try {
    return localStorage.getItem("token") || "";
  } catch {
    return "";
  }
};

const withAuthHeaders = (headers = {}) => {
  const t = getToken();
  return t ? { ...headers, Authorization: `Bearer ${t}` } : headers;
};

/**
 * timezone-safe ISO date "YYYY-MM-DD"
 */
function toISO(value) {
  try {
    if (!value) return null;

    if (typeof value?.format === "function") return value.format("YYYY-MM-DD");

    if (value instanceof Date) {
      if (isNaN(value.getTime())) return null;
      const y = value.getFullYear();
      const m = String(value.getMonth() + 1).padStart(2, "0");
      const d = String(value.getDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    }

    const dt = new Date(value);
    if (isNaN(dt.getTime())) return null;
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, "0");
    const d = String(dt.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  } catch {
    return null;
  }
}

function formatDateTime(value) {
  try {
    if (!value) return "";
    const dt = new Date(value);
    if (isNaN(dt.getTime())) return "";
    return dt.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

function formatMoneyEUR(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "";
  return `€${n.toFixed(0)}`;
}

function bookingTotalAmount(b) {
  const possible = [b?.total, b?.totalPrice, b?.amount, b?.paidAmount, b?.price];

  for (const v of possible) {
    const n = Number(v);
    if (Number.isFinite(n) && n >= 0) return n;
  }

  return null;
}

/* ✅ supports both old string image and new Cloudinary {url, publicId} */
function imageUrlOne(img) {
  if (!img) return "";
  if (typeof img === "string") return img;
  if (typeof img === "object" && typeof img.url === "string") return img.url;
  return "";
}

/* ✅ supports both: images: ["..."] and images: [{url, publicId}] */
function firstImageUrl(images) {
  const arr = Array.isArray(images) ? images : [];
  return imageUrlOne(arr[0]);
}

const MS_DAY = 24 * 60 * 60 * 1000;
function daysLeft(expiresAt) {
  if (!expiresAt) return null;
  const diff = new Date(expiresAt).getTime() - Date.now();
  return Math.ceil(diff / MS_DAY);
}

// ✅ normalize property ids + normalize images to URL strings for UI safety
function normalizeProperty(raw) {
  if (!raw || typeof raw !== "object") return raw;

  const id = raw.id ?? raw._id;
  const ownerId = raw.ownerId ?? raw.owner ?? raw.owner?._id;

  const imgsRaw = Array.isArray(raw.images) ? raw.images : [];
  const images = imgsRaw.map((img) => imageUrlOne(img)).filter(Boolean);

  return {
    ...raw,
    id: id != null ? String(id) : raw.id,
    ownerId: ownerId != null ? String(ownerId) : raw.ownerId,
    images,
  };
}

/* ✅ ALWAYS sort booking dates so UI / status / next arrival are correct */
function bookingDatesArray(b) {
  return (Array.isArray(b?.dates) ? b.dates : [])
    .map(toISO)
    .filter(Boolean)
    .sort((a, b2) => a.localeCompare(b2));
}

function bookingNights(b) {
  const ds = bookingDatesArray(b);
  return ds.length;
}

function bookingFirstDate(b) {
  const ds = bookingDatesArray(b);
  return ds[0] || "";
}

function bookingLastDate(b) {
  const ds = bookingDatesArray(b);
  return ds[ds.length - 1] || "";
}

function bookingStatus(b) {
  const explicit = String(b?.status || "").trim().toLowerCase();
  if (explicit === "cancelled_by_host" || explicit === "cancelled_by_admin") {
    return "cancelled";
  }

  const ds = bookingDatesArray(b);
  if (!ds.length) return "unknown";

  const today = toISO(new Date());
  if (!today) return "unknown";

  const from = ds[0];
  const to = ds[ds.length - 1];

  if (to < today) return "past";
  if (from > today) return "upcoming";
  return "active";
}

function bookingGuestCount(b) {
  const possible = [b?.guests, b?.guestCount, b?.numberOfGuests, b?.people, b?.persons, b?.adults];

  for (const v of possible) {
    const n = Number(v);
    if (Number.isFinite(n) && n > 0) return n;
  }

  return 0;
}

function bookingCreatedAt(b) {
  return b?.createdAt || b?.bookedAt || b?.dateCreated || b?.created_on || "";
}

function bookingIsNew(b) {
  const created = bookingCreatedAt(b);
  if (!created) return false;
  const dt = new Date(created);
  if (isNaN(dt.getTime())) return false;
  return Date.now() - dt.getTime() <= 48 * 60 * 60 * 1000;
}

function bookingSortWeight(status) {
  if (status === "active") return 0;
  if (status === "upcoming") return 1;
  if (status === "past") return 2;
  if (status === "cancelled") return 3;
  return 4;
}

async function copyTextSafe(text) {
  const value = String(text || "").trim();
  if (!value) return false;

  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return true;
    }
  } catch {}

  try {
    const ta = document.createElement("textarea");
    ta.value = value;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.top = "-1000px";
    ta.style.left = "-1000px";
    document.body.appendChild(ta);
    ta.select();
    document.execCommand("copy");
    document.body.removeChild(ta);
    return true;
  } catch {
    return false;
  }
}

export default function MyProperties({
  user,
  setUser,
  users,
  properties,
  setProperties,
  deleteProperty,
}) {
  const { t } = useTranslation();

  const [editingProperty, setEditingProperty] = useState(null);
  const [newImages, setNewImages] = useState([]);
  const [bookingsByPropertyId, setBookingsByPropertyId] = useState({});
  const [deletingById, setDeletingById] = useState({});
  const [copiedKey, setCopiedKey] = useState("");

  const [hostLoading, setHostLoading] = useState(false);
  const [hostError, setHostError] = useState("");

  const isHost = user?.role === "host";

  const normalizedProperties = useMemo(() => (properties || []).map(normalizeProperty), [properties]);

  const myProps = useMemo(() => {
    if (!user) return [];
    const myId = String(user.id || user._id || "");
    if (!myId) return [];
    return normalizedProperties.filter((p) => String(p.ownerId || "") === myId);
  }, [normalizedProperties, user]);

  const getBookingId = (b, idx) => b?.id ?? b?._id ?? `${idx}`;

  const bookingRangeText = (b) => {
    const ds = bookingDatesArray(b);
    if (ds.length === 0) return "";
    const from = ds[0];
    const to = ds[ds.length - 1];
    return from === to ? from : `${from} → ${to}`;
  };

  const propertyLocationText = (p) => {
    if (typeof p?.location === "string") return p.location;
    if (p?.location?.text) return p.location.text;
    return t("myProperties.location.unknown");
  };

  const startingPrice = (p) => {
    const firstRoom = (p.rooms || [])[0];
    const rp = Number(firstRoom?.price || 0);
    if (rp > 0) return rp;
    return Number(p.price || 0);
  };

  const becomeHost = async () => {
    if (hostLoading) return;
    setHostError("");

    try {
      setHostLoading(true);

      const res = await fetch(`${API_URL}/api/auth/become-host`, {
        method: "POST",
        credentials: "include",
        headers: withAuthHeaders(),
      });

      let data = {};
      const contentType = res.headers.get("content-type") || "";
      if (contentType.includes("application/json")) data = await res.json();
      else data = { message: await res.text() };

      if (!res.ok) {
        setHostError(data?.message || t("myProperties.errors.failedWithStatus", { status: res.status }));
        return;
      }

      if (data?.user) {
        if (typeof setUser === "function") setUser(data.user);
      }
      if (data?.token) {
        try {
          localStorage.setItem("token", data.token);
        } catch {}
      }
    } catch (e) {
      console.error(e);
      setHostError(t("myProperties.errors.networkBecomingHost"));
    } finally {
      setHostLoading(false);
    }
  };

  useEffect(() => {
    if (!copiedKey) return;
    const timer = setTimeout(() => setCopiedKey(""), 1400);
    return () => clearTimeout(timer);
  }, [copiedKey]);

  useEffect(() => {
    if (!user || !isHost || myProps.length === 0) return;

    let cancelled = false;

    const load = async () => {
      setBookingsByPropertyId((prev) => {
        const next = { ...prev };
        for (const p of myProps) {
          next[p.id] = {
            loading: true,
            error: null,
            bookings: next[p.id]?.bookings || [],
          };
        }
        return next;
      });

      await Promise.all(
        myProps.map(async (p) => {
          try {
            const res = await fetch(`${API_URL}/api/property/${p.id}/bookings`, {
              credentials: "include",
              headers: withAuthHeaders(),
            });

            if (!res.ok) {
              if (!cancelled) {
                setBookingsByPropertyId((prev) => ({
                  ...prev,
                  [p.id]: {
                    loading: false,
                    error: t("myProperties.errors.loadErrorWithStatus", {
                      status: res.status,
                    }),
                    bookings: prev[p.id]?.bookings || [],
                  },
                }));
              }
              return;
            }

            const data = await res.json();
            const bookings = Array.isArray(data) ? data : data?.bookings || [];

            if (!cancelled) {
              setBookingsByPropertyId((prev) => ({
                ...prev,
                [p.id]: { loading: false, error: null, bookings },
              }));
            }
          } catch {
            if (!cancelled) {
              setBookingsByPropertyId((prev) => ({
                ...prev,
                [p.id]: {
                  loading: false,
                  error: t("myProperties.errors.networkError"),
                  bookings: prev[p.id]?.bookings || [],
                },
              }));
            }
          }
        })
      );
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [user, isHost, myProps, t]);

  const cancelBooking = async (propertyId, bookingId) => {
    if (!window.confirm(t("myProperties.confirm.cancelBooking"))) return;

    try {
      const res = await fetch(`${API_URL}/api/property/${propertyId}/bookings/${bookingId}`, {
        method: "DELETE",
        credentials: "include",
        headers: withAuthHeaders(),
      });

      let data = {};
      const contentType = res.headers.get("content-type") || "";
      if (contentType.includes("application/json")) data = await res.json();
      else data = { message: await res.text() };

      if (!res.ok || !data.success) {
        alert(
          data?.error ||
            data?.message ||
            t("myProperties.errors.cancelFailedWithStatus", {
              status: res.status,
            })
        );
        return;
      }

      if (data.property) {
        const updated = normalizeProperty(data.property);
        setProperties((prev) =>
          (prev || []).map((p) =>
            String(p.id || p._id) === updated.id ? updated : p
          )
        );
        setBookingsByPropertyId((prev) => ({
          ...prev,
          [propertyId]: {
            loading: false,
            error: null,
            bookings: updated.bookings || [],
          },
        }));
        setEditingProperty((prev) =>
          prev && String(prev.id || prev._id) === updated.id ? updated : prev
        );
      }
    } catch (e) {
      console.error(e);
      alert(t("myProperties.errors.networkCancelBooking"));
    }
  };

  const renewProperty = async (propertyId) => {
    try {
      const res = await fetch(`${API_URL}/api/properties/${propertyId}/renew`, {
        method: "PUT",
        credentials: "include",
        headers: withAuthHeaders(),
      });

      const data = await res.json();

      if (!res.ok || !data.success || !data.property) {
        alert(
          data?.error ||
            t("myProperties.errors.renewFailedWithStatus", {
              status: res.status,
            })
        );
        return;
      }

      const updated = normalizeProperty(data.property);
      setProperties((prev) =>
        (prev || []).map((p) =>
          String(p.id || p._id) === updated.id ? updated : p
        )
      );
      alert(t("myProperties.success.renewed30"));
    } catch (e) {
      console.error(e);
      alert(t("myProperties.errors.networkRenewProperty"));
    }
  };

  const handleRoomChange = (roomId, key, value) => {
    setEditingProperty((prev) => ({
      ...prev,
      rooms: (prev.rooms || []).map((r) =>
        String(r.id) === String(roomId) ? { ...r, [key]: value } : r
      ),
    }));
  };

  const addRoom = () => {
    const nextId = Date.now();
    setEditingProperty((prev) => ({
      ...prev,
      rooms: [
        ...(prev.rooms || []),
        {
          id: nextId,
          type: "Single",
          price: 50,
          availableDates: [],
          reservedDates: [],
        },
      ],
    }));
  };

  const removeRoom = (roomId) => {
    setEditingProperty((prev) => ({
      ...prev,
      rooms: (prev.rooms || []).filter((r) => String(r.id) !== String(roomId)),
    }));
  };

  const handleImageChange = (e) => setNewImages([...(e.target.files || [])]);

  const handleSave = async () => {
    if (!editingProperty) return;

    const formattedRooms = (editingProperty.rooms || []).map((r) => ({
      ...r,
      price: Number(r.price || 0),
      availableDates: (r.availableDates || []).map(toISO).filter(Boolean),
      reservedDates: Array.isArray(r.reservedDates)
        ? r.reservedDates.map(toISO).filter(Boolean)
        : [],
    }));

    const formData = new FormData();
    formData.append("title", editingProperty.title || "");
    const locText =
      typeof editingProperty.location === "string"
        ? editingProperty.location
        : editingProperty.location?.text || "";
    formData.append("location", locText);

    formData.append("price", String(editingProperty.price || 0));
    formData.append("description", editingProperty.description || "");
    formData.append("wifi", editingProperty.wifi ? "true" : "false");
    formData.append("parking", editingProperty.parking ? "true" : "false");
    formData.append("pool", editingProperty.pool ? "true" : "false");

    formData.append("rooms", JSON.stringify(formattedRooms));
    newImages.forEach((img) => formData.append("images", img));

    try {
      const res = await fetch(`${API_URL}/api/properties/${editingProperty.id}`, {
        method: "PUT",
        body: formData,
        credentials: "include",
        headers: withAuthHeaders(),
      });

      const data = await res.json();

      if (!res.ok || !data.property) {
        alert(
          data?.error ||
            t("myProperties.errors.saveFailedWithStatus", {
              status: res.status,
            })
        );
        return;
      }

      const updated = normalizeProperty(data.property);
      setProperties((prev) =>
        (prev || []).map((p) =>
          String(p.id || p._id) === updated.id ? updated : p
        )
      );
      setEditingProperty(updated);
      setNewImages([]);
    } catch (e) {
      console.error(e);
      alert(t("myProperties.errors.errorSavingProperty"));
    }
  };

  const openEdit = async (property) => {
    window.scrollTo({ top: 0, behavior: "smooth" });

    if (!isHost) {
      setEditingProperty(property);
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/property/${property.id}/bookings`, {
        credentials: "include",
        headers: withAuthHeaders(),
      });

      const data = await res.json();
      const bookings = Array.isArray(data) ? data : data?.bookings || [];

      const rooms = (property.rooms || []).map((r) => ({
        ...r,
        price: Number(r.price || 0),
        availableDates: (r.availableDates || []).map(toISO).filter(Boolean),
        reservedDates: Array.isArray(r.reservedDates)
          ? r.reservedDates.map(toISO).filter(Boolean)
          : [],
      }));

      const indexByRoomId = new Map(rooms.map((r, idx) => [String(r.id), idx]));

      for (const b of bookings) {
        if (bookingStatus(b) === "cancelled") continue;

        const rid = b?.roomId;
        const days = bookingDatesArray(b);
        if (!rid || days.length === 0) continue;

        const idx = indexByRoomId.get(String(rid));
        if (idx === undefined) continue;

        rooms[idx].reservedDates = Array.from(
          new Set([...(rooms[idx].reservedDates || []), ...days])
        );
      }

      setEditingProperty({ ...property, rooms });

      setTimeout(() => {
        const body = document.querySelector("[data-modal-body='mp-edit']");
        if (body) body.scrollTop = 0;
      }, 0);
    } catch (e) {
      console.error(e);
      setEditingProperty(property);
    }
  };

  const mapDaysForRoom = (roomId) => ({ date }) => {
    if (!editingProperty || !date) return {};
    const iso = toISO(date);
    if (!iso) return {};

    const room = (editingProperty.rooms || []).find(
      (r) => String(r.id) === String(roomId)
    );
    if (!room) return {};

    const reservedSet = new Set((room.reservedDates || []).map(toISO).filter(Boolean));
    const availableSet = new Set((room.availableDates || []).map(toISO).filter(Boolean));

    if (reservedSet.has(iso)) {
      return {
        disabled: true,
        style: {
          backgroundColor: "#ffe4e6",
          borderRadius: 10,
          color: "#9f1239",
          textDecoration: "line-through",
        },
      };
    }

    if (availableSet.has(iso)) {
      return {
        style: {
          backgroundColor: "#e0f2fe",
          borderRadius: 10,
          color: "#075985",
        },
      };
    }

    return {};
  };

  const apiDeleteProperty = async (propertyId) => {
    if (!propertyId) return;
    if (deletingById[propertyId]) return;

    const ok = window.confirm(t("myProperties.confirm.deleteProperty"));
    if (!ok) return;

    try {
      setDeletingById((prev) => ({ ...prev, [propertyId]: true }));

      if (typeof deleteProperty === "function") {
        const result = await deleteProperty(propertyId);
        if (result?.success === false) {
          alert(result?.message || t("myProperties.errors.deleteFailed"));
          return;
        }

        setProperties((prev) =>
          (prev || []).filter((p) => String(p.id || p._id) !== String(propertyId))
        );
        setBookingsByPropertyId((prev) => {
          const next = { ...prev };
          delete next[propertyId];
          return next;
        });
        if (editingProperty?.id === propertyId) setEditingProperty(null);
        return;
      }

      const res = await fetch(`${API_URL}/api/properties/${propertyId}`, {
        method: "DELETE",
        credentials: "include",
        headers: withAuthHeaders(),
      });

      let data = {};
      const contentType = res.headers.get("content-type") || "";
      if (contentType.includes("application/json")) data = await res.json();
      else data = { message: await res.text() };

      if (!res.ok) {
        if (res.status === 401)
          alert(data.message || t("myProperties.errors.pleaseLoginAgain"));
        else if (res.status === 403)
          alert(data.message || t("myProperties.errors.notAllowedDelete"));
        else if (res.status === 404)
          alert(data.message || t("myProperties.errors.propertyNotFound"));
        else
          alert(
            data.message ||
              t("myProperties.errors.deleteFailedWithStatus", {
                status: res.status,
              })
          );
        return;
      }

      setProperties((prev) =>
        (prev || []).filter((p) => String(p.id || p._id) !== String(propertyId))
      );
      setBookingsByPropertyId((prev) => {
        const next = { ...prev };
        delete next[propertyId];
        return next;
      });
      if (editingProperty?.id === propertyId) setEditingProperty(null);
    } catch (e) {
      console.error(e);
      alert(t("myProperties.errors.networkDeleteProperty"));
    } finally {
      setDeletingById((prev) => {
        const next = { ...prev };
        delete next[propertyId];
        return next;
      });
    }
  };

  const handleCopy = async (type, value, bookingId) => {
    const ok = await copyTextSafe(value);
    if (ok) {
      setCopiedKey(`${type}-${bookingId}`);
      return;
    }
    alert("Could not copy.");
  };

  if (!user) return <p className="mp-simpleState">{t("myProperties.auth.loginToSee")}</p>;

  if (!isHost) {
    return (
      <div className="mp-page">
        <div className="mp-shell">
          <div className="mp-headerRow">
            <div>
              <h2 className="mp-h2">{t("myProperties.title")}</h2>
              <div className="mp-sub">{t("myProperties.nonHost.subtitle")}</div>
            </div>
          </div>

          <div className="mp-ctaCard">
            <h3 className="mp-ctaTitle">{t("myProperties.nonHost.ctaTitle")}</h3>
            <div className="mp-ctaSub">{t("myProperties.nonHost.ctaSub")}</div>

            {hostError && <div className="mp-hostError">{hostError}</div>}

            <button
              type="button"
              onClick={becomeHost}
              className="mp-ctaBtn"
              disabled={hostLoading}
            >
              {hostLoading
                ? t("myProperties.nonHost.upgrading")
                : t("myProperties.nonHost.becomeHost")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (myProps.length === 0) {
    return <p className="mp-simpleState">{t("myProperties.empty.noProperties")}</p>;
  }

  return (
    <div className="mp-page">
      <div className="mp-shell">
        <div className="mp-headerRow">
          <div>
            <h2 className="mp-h2">{t("myProperties.title")}</h2>
            <div className="mp-sub">{t("myProperties.subtitle")}</div>
          </div>
        </div>

        {editingProperty && (
          <div
            className="mp-overlay"
            onMouseDown={(e) => e.target === e.currentTarget && setEditingProperty(null)}
          >
            <div className="mp-modal">
              <div className="mp-modalHead">
                <h3 className="mp-modalTitle">{t("myProperties.edit.title")}</h3>
                <button
                  type="button"
                  className="mp-btnGhost"
                  onClick={() => setEditingProperty(null)}
                >
                  {t("myProperties.actions.close")}
                </button>
              </div>

              <div className="mp-modalBody" data-modal-body="mp-edit">
                <div className="mp-row2">
                  <div>
                    <div className="mp-fieldLabel">{t("myProperties.edit.fields.title")}</div>
                    <input
                      className="mp-input"
                      value={editingProperty.title || ""}
                      placeholder={t("myProperties.edit.placeholders.title")}
                      onChange={(e) =>
                        setEditingProperty({ ...editingProperty, title: e.target.value })
                      }
                    />
                  </div>

                  <div>
                    <div className="mp-fieldLabel">{t("myProperties.edit.fields.location")}</div>
                    <input
                      className="mp-input"
                      value={
                        typeof editingProperty.location === "string"
                          ? editingProperty.location
                          : editingProperty.location?.text || ""
                      }
                      placeholder={t("myProperties.edit.placeholders.locationText")}
                      onChange={(e) =>
                        setEditingProperty({ ...editingProperty, location: e.target.value })
                      }
                    />
                    <div className="mp-muted">{t("myProperties.edit.tips.locationFromAddProperty")}</div>
                  </div>
                </div>

                <div className="mp-mt10">
                  <div className="mp-fieldLabel">{t("myProperties.edit.fields.basePriceOptional")}</div>
                  <input
                    className="mp-input"
                    type="number"
                    value={editingProperty.price || 0}
                    placeholder={t("myProperties.edit.placeholders.basePrice")}
                    onChange={(e) =>
                      setEditingProperty({
                        ...editingProperty,
                        price: Number(e.target.value),
                      })
                    }
                  />
                  <div className="mp-muted">{t("myProperties.edit.tips.guestsUseRoomPrices")}</div>
                </div>

                <div className="mp-mt10">
                  <div className="mp-fieldLabel">{t("myProperties.edit.fields.description")}</div>
                  <textarea
                    className="mp-textarea"
                    value={editingProperty.description || ""}
                    placeholder={t("myProperties.edit.placeholders.description")}
                    rows={4}
                    onChange={(e) =>
                      setEditingProperty({
                        ...editingProperty,
                        description: e.target.value,
                      })
                    }
                  />
                </div>

                <div className="mp-mt10">
                  <div className="mp-fieldLabel">{t("myProperties.edit.fields.amenities")}</div>

                  <div className="mp-pillRow">
                    <div
                      className={`mp-pill ${editingProperty.wifi ? "is-on" : ""}`}
                      onClick={() => setEditingProperty((p) => ({ ...p, wifi: !p.wifi }))}
                    >
                      📶 {t("myProperties.amenities.wifi")}
                    </div>

                    <div
                      className={`mp-pill ${editingProperty.parking ? "is-on" : ""}`}
                      onClick={() => setEditingProperty((p) => ({ ...p, parking: !p.parking }))}
                    >
                      🅿️ {t("myProperties.amenities.parking")}
                    </div>

                    <div
                      className={`mp-pill ${editingProperty.pool ? "is-on" : ""}`}
                      onClick={() => setEditingProperty((p) => ({ ...p, pool: !p.pool }))}
                    >
                      🏊 {t("myProperties.amenities.pool")}
                    </div>

                    <label className="mp-pill mp-pillFile">
                      📷 {t("myProperties.actions.addImages")}
                      <input
                        type="file"
                        multiple
                        onChange={handleImageChange}
                        style={{ display: "none" }}
                      />
                    </label>
                  </div>
                </div>

                <div className="mp-roomsSection">
                  <div className="mp-roomsHeader">
                    <div>
                      <div className="mp-roomsTitle">{t("myProperties.rooms.title")}</div>
                      <div className="mp-muted">{t("myProperties.rooms.legend")}</div>
                    </div>

                    <div
                      role="button"
                      tabIndex={0}
                      className="mp-addTile"
                      onClick={addRoom}
                      onKeyDown={(e) => e.key === "Enter" && addRoom()}
                    >
                      <span className="mp-plus">＋</span>
                      <span>{t("myProperties.rooms.addRoom")}</span>
                    </div>
                  </div>

                  {(editingProperty.rooms || []).map((room, idx) => (
                    <div key={room.id} className="mp-roomCard">
                      <div className="mp-roomHead">
                        <div className="mp-roomHeadLeft">
                          <span className="mp-roomBadge">{idx + 1}</span>
                          <div className="mp-roomName">{t("myProperties.rooms.roomN", { n: idx + 1 })}</div>
                        </div>

                        {(editingProperty.rooms || []).length > 1 && (
                          <button
                            type="button"
                            className="mp-dangerLink"
                            onClick={() => removeRoom(room.id)}
                          >
                            {t("myProperties.actions.remove")}
                          </button>
                        )}
                      </div>

                      <div className="mp-row3">
                        <div>
                          <div className="mp-fieldLabel">{t("myProperties.rooms.fields.type")}</div>
                          <select
                            value={room.type}
                            onChange={(e) => handleRoomChange(room.id, "type", e.target.value)}
                            className="mp-input"
                          >
                            <option value="Single">{t("myProperties.rooms.types.single")}</option>
                            <option value="Double">{t("myProperties.rooms.types.double")}</option>
                            <option value="Triple">{t("myProperties.rooms.types.triple")}</option>
                          </select>
                        </div>

                        <div>
                          <div className="mp-fieldLabel">{t("myProperties.rooms.fields.priceEur")}</div>
                          <input
                            className="mp-input"
                            type="number"
                            min={0}
                            value={Number(room.price || 0)}
                            onChange={(e) => handleRoomChange(room.id, "price", Number(e.target.value))}
                          />
                        </div>

                        <div className="mp-roomStats">
                          <div className="mp-muted">
                            {t("myProperties.rooms.reservedDays", {
                              n: (room.reservedDates || []).length,
                            })}
                          </div>
                          <div className="mp-muted">
                            {t("myProperties.rooms.availableDays", {
                              n: (room.availableDates || []).length,
                            })}
                          </div>
                        </div>
                      </div>

                      <div className="mp-dateWrap">
                        <DatePicker
                          multiple
                          value={room.availableDates || []}
                          onChange={(dates) => handleRoomChange(room.id, "availableDates", dates || [])}
                          format="YYYY/MM/DD"
                          className="teal"
                          mapDays={mapDaysForRoom(room.id)}
                          monthsShown={2}
                          calendarPosition="bottom-center"
                          portal
                          zIndex={1000000}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mp-footerRow">
                <button
                  type="button"
                  className="mp-btnGhost"
                  onClick={() => setEditingProperty(null)}
                >
                  {t("myProperties.actions.cancel")}
                </button>
                <button type="button" className="mp-btn mp-btnPrimary" onClick={handleSave}>
                  {t("myProperties.actions.saveChanges")}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="mp-grid">
          {myProps.map((p) => {
            const bState = bookingsByPropertyId[p.id];
            const bookings = bState?.bookings || [];
            const loading = !!bState?.loading;
            const error = bState?.error;

            const sortedBookings = [...bookings].sort((a, b) => {
              const aStatus = bookingStatus(a);
              const bStatus = bookingStatus(b);

              const byStatus = bookingSortWeight(aStatus) - bookingSortWeight(bStatus);
              if (byStatus !== 0) return byStatus;

              const aDate = bookingFirstDate(a);
              const bDate = bookingFirstDate(b);
              const byStart = String(aDate).localeCompare(String(bDate));
              if (byStart !== 0) return byStart;

              const aCreated = new Date(bookingCreatedAt(a) || 0).getTime();
              const bCreated = new Date(bookingCreatedAt(b) || 0).getTime();
              return bCreated - aCreated;
            });

            const roomById = new Map((p.rooms || []).map((r) => [String(r.id), r]));

            const left = daysLeft(p.expiresAt);
            const expired = left !== null && left <= 0;
            const expiringSoon = left !== null && left <= 3 && !expired;

            const toneClass = expired ? "is-danger" : expiringSoon ? "is-warn" : "is-ok";

            const isDeleting = !!deletingById[p.id];

            const ratingValue = Number(p.rating || 0);
            const ratingText = ratingValue > 0 ? ratingValue.toFixed(1) : null;

            const statusText =
              left === null
                ? t("myProperties.expiry.noExpiry")
                : expired
                ? t("myProperties.expiry.expired")
                : expiringSoon
                ? t("myProperties.expiry.expiringSoon")
                : t("myProperties.expiry.active");

            const daysText =
              left === null
                ? ""
                : expired
                ? t("myProperties.expiry.daysAgo", { n: Math.abs(left) })
                : t("myProperties.expiry.daysLeft", { n: left });

            const activeLikeBookings = bookings.filter((b) => bookingStatus(b) !== "cancelled");

            const totalBookings = activeLikeBookings.length;
            const totalBookedNights = activeLikeBookings.reduce((sum, b) => sum + bookingNights(b), 0);
            const upcomingCount = activeLikeBookings.filter((b) => bookingStatus(b) === "upcoming").length;
            const activeCount = activeLikeBookings.filter((b) => bookingStatus(b) === "active").length;
            const nextUpcoming = activeLikeBookings.find((b) => bookingStatus(b) === "upcoming");
            const nextUpcomingLabel = nextUpcoming ? bookingRangeText(nextUpcoming) : null;

            return (
              <div key={p.id} className="mp-card">
                <div className="mp-imgWrap">
                  <img
                    src={firstImageUrl(p.images) || "/placeholder.png"}
                    alt={p.title || t("myProperties.card.propertyAlt")}
                    className="mp-img"
                  />

                  <div className="mp-chip">{propertyLocationText(p)}</div>

                  <div className="mp-topRightStack">
                    <div className="mp-imageBadge">
                      🗓 {totalBookings}{" "}
                      {t("myProperties.bookings.title", { defaultValue: "bookings" })}
                    </div>

                    {activeCount > 0 && (
                      <div className="mp-imageBadge">
                        🔔 {activeCount}{" "}
                        {t("myProperties.bookings.activeNow", {
                          defaultValue: "active now",
                        })}
                      </div>
                    )}
                  </div>
                </div>

                <div className="mp-body">
                  <div className="mp-topRow">
                    <h3 className="mp-title">{p.title || t("myProperties.card.noTitle")}</h3>
                    <div className="mp-price">
                      {t("myProperties.card.fromPrice", {
                        price: startingPrice(p) || 0,
                      })}
                    </div>
                  </div>

                  <div className="mp-metaRow">
                    <div className="mp-metaLeft">
                      {ratingText ? (
                        <div className="mp-ratingWrap">
                          <span>⭐</span>
                          <span>{ratingText}</span>
                        </div>
                      ) : (
                        <div className="mp-softPill">
                          {t("myProperties.card.noRatingYet", {
                            defaultValue: "No rating yet",
                          })}
                        </div>
                      )}

                      <div className="mp-softPill">
                        🏠 {(p.rooms || []).length}{" "}
                        {t("myProperties.rooms.title", { defaultValue: "rooms" })}
                      </div>
                    </div>
                  </div>

                  <div className="mp-statusRow">
                    <div className="mp-statusLeft">
                      {left === null ? (
                        <span className="mp-badge is-warn">{t("myProperties.expiry.noExpiry")}</span>
                      ) : (
                        <span className={`mp-badge ${toneClass}`}>
                          {statusText} · {daysText}
                        </span>
                      )}
                    </div>

                    <button
                      type="button"
                      onClick={() => renewProperty(p.id)}
                      className={`mp-renewBtn ${expiringSoon || expired ? "is-danger" : "is-dark"}`}
                      title={t("myProperties.bookings.renewTitle")}
                    >
                      {t("myProperties.bookings.renew")}
                    </button>
                  </div>

                  <div className="mp-statsGrid">
                    <div className="mp-statCard">
                      <div className="mp-statLabel">
                        {t("myProperties.stats.totalBookings", {
                          defaultValue: "Total bookings",
                        })}
                      </div>
                      <div className="mp-statValue">{totalBookings}</div>
                    </div>

                    <div className="mp-statCard">
                      <div className="mp-statLabel">
                        {t("myProperties.stats.nightsBooked", {
                          defaultValue: "Booked nights",
                        })}
                      </div>
                      <div className="mp-statValue">{totalBookedNights}</div>
                    </div>

                    <div className="mp-statCard">
                      <div className="mp-statLabel">
                        {t("myProperties.stats.upcoming", {
                          defaultValue: "Upcoming",
                        })}
                      </div>
                      <div className="mp-statValue">{upcomingCount}</div>
                    </div>
                  </div>

                  <div className="mp-quickInfo">
                    <div className="mp-quickInfoTitle">
                      {t("myProperties.bookings.hostOverview", {
                        defaultValue: "Host overview",
                      })}
                    </div>
                    <div className="mp-muted">
                      {nextUpcomingLabel
                        ? t("myProperties.bookings.nextArrival", {
                            defaultValue: `Next arrival: ${nextUpcomingLabel}`,
                            date: nextUpcomingLabel,
                          })
                        : t("myProperties.bookings.noUpcomingArrival", {
                            defaultValue: "No upcoming arrivals yet.",
                          })}
                    </div>
                  </div>

                  <div className="mp-section">
                    <div className="mp-sectionHead">
                      <div className="mp-sectionTitle">{t("myProperties.bookings.title")}</div>
                    </div>

                    {loading && <div className="mp-muted">{t("myProperties.bookings.loading")}</div>}

                    {error && <div className="mp-errorText">{error}</div>}

                    {!loading && !error && bookings.length === 0 && (
                      <div className="mp-emptyBookingsBox">
                        {t("myProperties.bookings.noneYet")}
                      </div>
                    )}

                    {!loading && !error && bookings.length > 0 && (
                      <div className="mp-bookingList">
                        {sortedBookings.map((b, idx) => {
                          const room = roomById.get(String(b.roomId));
                          const roomText = room
                            ? t("myProperties.bookings.roomTypeAndId", {
                                type: room.type,
                                id: room.id,
                              })
                            : t("myProperties.bookings.roomIdOnly", { id: b.roomId });

                          const bookingId = getBookingId(b, idx);
                          const guestName = b?.name || t("myProperties.bookings.unknown");
                          const phone = b?.phone || t("myProperties.bookings.na");
                          const email = b?.email || b?.guestEmail || "";
                          const guestCount = bookingGuestCount(b);
                          const notes = b?.notes || b?.message || b?.specialRequests || "";
                          const status = bookingStatus(b);
                          const createdAtText = formatDateTime(bookingCreatedAt(b));
                          const isNewBooking = bookingIsNew(b);
                          const bookingFrom = bookingFirstDate(b);
                          const bookingTo = bookingLastDate(b);
                          const totalAmount = bookingTotalAmount(b);
                          const totalAmountText = totalAmount !== null ? formatMoneyEUR(totalAmount) : "";
                          const cancelledAtText = formatDateTime(b?.cancelledAt || "");
                          const dateRangeLabel =
                            bookingFrom && bookingTo
                              ? bookingFrom === bookingTo
                                ? bookingFrom
                                : `${bookingFrom} → ${bookingTo}`
                              : bookingRangeText(b);

                          const statusClass =
                            status === "active"
                              ? "is-active"
                              : status === "upcoming"
                              ? "is-upcoming"
                              : status === "cancelled"
                              ? "is-past"
                              : "is-past";

                          const canCancel = status !== "cancelled";

                          return (
                            <div key={bookingId} className="mp-bookingItem">
                              <div className="mp-bookingTop">
                                <div className="mp-flex1 mp-min0">
                                  <div className="mp-bookingMeta" style={{ marginTop: 0 }}>
                                    <span className={`mp-bookingTag ${statusClass}`}>
                                      {status === "active"
                                        ? t("myProperties.bookings.activeNow", {
                                            defaultValue: "Active now",
                                          })
                                        : status === "upcoming"
                                        ? t("myProperties.bookings.upcoming", {
                                            defaultValue: "Upcoming",
                                          })
                                        : status === "cancelled"
                                        ? "Cancelled"
                                        : t("myProperties.bookings.past", {
                                            defaultValue: "Past",
                                          })}
                                    </span>

                                    <span className="mp-bookingTag is-upcoming">{dateRangeLabel}</span>

                                    <span className="mp-bookingTag is-past">
                                      {bookingNights(b)}{" "}
                                      {t("myProperties.bookings.nights", {
                                        defaultValue: "nights",
                                      })}
                                    </span>

                                    <span className="mp-bookingTag is-past">{roomText}</span>

                                    {totalAmountText ? (
                                      <span className="mp-bookingTag is-active">💶 {totalAmountText}</span>
                                    ) : null}

                                    {isNewBooking ? (
                                      <span className="mp-bookingTag is-upcoming">
                                        ✨{" "}
                                        {t("myProperties.bookings.newBooking", {
                                          defaultValue: "New booking",
                                        })}
                                      </span>
                                    ) : null}

                                    {notes ? (
                                      <span className="mp-bookingTag is-active">
                                        📝{" "}
                                        {t("myProperties.bookings.hasMessage", {
                                          defaultValue: "Has message",
                                        })}
                                      </span>
                                    ) : null}
                                  </div>

                                  <div className="mp-guestTitle" style={{ marginTop: 10 }}>
                                    {guestName}
                                  </div>

                                  <div className="mp-guestSub">
                                    <span>📞 {phone}</span>
                                    {email ? <span>✉️ {email}</span> : null}
                                    {guestCount > 0 ? (
                                      <span>
                                        👥 {guestCount}{" "}
                                        {t("myProperties.bookings.guests", {
                                          defaultValue: "guests",
                                        })}
                                      </span>
                                    ) : null}
                                    {totalAmountText ? <span>💶 {totalAmountText}</span> : null}
                                  </div>
                                </div>

                                <button
                                  type="button"
                                  onClick={() => canCancel && cancelBooking(p.id, bookingId)}
                                  className="mp-cancelBtn"
                                  disabled={!canCancel}
                                  style={!canCancel ? { opacity: 0.55, cursor: "not-allowed" } : undefined}
                                >
                                  {canCancel ? t("myProperties.actions.cancelBooking") : "Cancelled"}
                                </button>
                              </div>

                              <div className="mp-guestSub" style={{ marginTop: 10 }}>
                                <span>🆔 #{bookingId}</span>
                                {createdAtText ? (
                                  <span>
                                    🕒{" "}
                                    {t("myProperties.bookings.createdAt", {
                                      defaultValue: `Booked: ${createdAtText}`,
                                      date: createdAtText,
                                    })}
                                  </span>
                                ) : null}
                                {status === "cancelled" && cancelledAtText ? <span>❌ Cancelled: {cancelledAtText}</span> : null}
                              </div>

                              {(phone || email) && (
                                <div className="mp-bookingMeta" style={{ marginTop: 10 }}>
                                  {phone ? (
                                    <button
                                      type="button"
                                      className="mp-btnGhost"
                                      onClick={() => handleCopy("phone", phone, bookingId)}
                                      title="Copy phone"
                                    >
                                      {copiedKey === `phone-${bookingId}` ? "Copied phone" : "Copy phone"}
                                    </button>
                                  ) : null}

                                  {email ? (
                                    <button
                                      type="button"
                                      className="mp-btnGhost"
                                      onClick={() => handleCopy("email", email, bookingId)}
                                      title="Copy email"
                                    >
                                      {copiedKey === `email-${bookingId}` ? "Copied email" : "Copy email"}
                                    </button>
                                  ) : null}
                                </div>
                              )}

                              {notes ? <div className="mp-notesBox">📝 {notes}</div> : null}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <div className="mp-btnRow">
                    <button
                      type="button"
                      className="mp-btn mp-btnPrimary"
                      onClick={() => openEdit(p)}
                      disabled={isDeleting}
                    >
                      {t("myProperties.actions.edit")}
                    </button>

                    <button
                      type="button"
                      className={`mp-btn mp-btnDark ${isDeleting ? "is-disabled" : ""}`}
                      onClick={() => apiDeleteProperty(p.id)}
                      disabled={isDeleting}
                      title={
                        isDeleting
                          ? t("myProperties.actions.deleting")
                          : t("myProperties.actions.delete")
                      }
                    >
                      {isDeleting ? t("myProperties.actions.deleting") : t("myProperties.actions.delete")}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mp-bottomSpacer" />
      </div>
    </div>
  );
}